import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtDateTime, fmtFileSize } from '../utils/formatters';
import toast from 'react-hot-toast';
import { Upload, Download, Trash2, FileText, Image, File, Eye } from 'lucide-react';
import './DocumentUpload.css';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8081';
const fileUrl = (path) => path?.startsWith("http") ? path : `${API_BASE}${path}`;

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx';
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

function getFileIcon(mimeType) {
  if (IMAGE_TYPES.includes(mimeType)) return <Image size={16} />;
  if (mimeType === 'application/pdf')  return <FileText size={16} />;
  return <File size={16} />;
}

export default function DocumentUpload({ leadId, canEdit }) {
  const { user } = useAuth();
  const [docs,        setDocs]        = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const [uploadPct,   setUploadPct]   = useState(0);
  const [dragging,    setDragging]    = useState(false);
  const [preview,     setPreview]     = useState(null); // { url, name }
  const fileInputRef = useRef(null);

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setDocs(Array.isArray(data.documents) ? data.documents : []);
    } catch { /* silently fail */ }
  }, [leadId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadPct(10);
    try {
      // 1. Upload to Cloudinary via /upload
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setUploadPct(10 + Math.round((e.loaded / e.total) * 40)),
      });
      const url = res.data.url;

      // 2. Link to lead
      setUploadPct(70);
      await api.post(`/leads/${leadId}/documents`, {
        url,
        name: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream'
      });
      
      setUploadPct(100);
      await loadDocs();
      toast.success('Document uploaded!');
    } catch (err) {
      console.error("Upload error details:", err?.response?.data || err);
      toast.error(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = ()  => setDragging(false);
  const onDrop      = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDelete = async (docId, docName) => {
    if (!window.confirm(`Delete "${docName}"?`)) return;
    try {
      await api.delete(`/leads/${leadId}/documents/${docId}`);
      setDocs(d => d.filter(doc => doc.id !== docId && doc._id !== docId));
      toast.success('Document deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleDownload = (doc) => {
    let dlUrl = fileUrl(doc.url);
    if(dlUrl.includes("cloudinary.com")) dlUrl = dlUrl.replace("/upload/", "/upload/fl_attachment/");
    window.location.href = dlUrl;
  };

  const handlePreview = (doc) => {
    if (IMAGE_TYPES.includes(doc.mimeType) || doc.mimeType === 'application/pdf') {
      setPreview({ url: fileUrl(doc.url), name: doc.name, mimeType: doc.mimeType });
    } else {
      handleDownload(doc);
    }
  };

  const isUploader = (doc) => String(doc.uploadedBy) === String(user?.id);
  const canDelete  = (doc) => user?.role === 'Admin' || isUploader(doc);

  return (
    <div className="docup">
      {canEdit && (
        <div
          className={`docup-dropzone ${dragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
          {uploading ? (
            <div className="docup-progress">
              <div className="docup-progress-bar" style={{ width: `${uploadPct}%` }} />
              <span className="docup-progress-label">Uploading… {uploadPct}%</span>
            </div>
          ) : (
            <>
              <Upload size={20} className="docup-upload-icon" />
              <div className="docup-drop-text">
                <strong>Drop file here</strong> or click to browse
              </div>
            </>
          )}
        </div>
      )}

      {docs.length === 0 ? (
        <div className="docup-empty" style={{ color: 'var(--muted)' }}>No documents uploaded yet.</div>
      ) : (
        <div className="docup-list">
          {docs.map(doc => {
            const docId = doc.id || doc._id;
            return (
            <div key={docId} className="docup-item" style={{ background: 'var(--surface-2)', borderColor: 'var(--border)', color: 'var(--text)' }}>
              <div className="docup-item__icon text-primary">{getFileIcon(doc.mimeType)}</div>
              <div className="docup-item__info">
                <div className="docup-item__name" title={doc.name}>{doc.name}</div>
                <div className="docup-item__meta" style={{ color: 'var(--muted)' }}>
                  {fmtFileSize(doc.fileSize)} · {doc.uploadedByName || doc.uploadedBy} · {fmtDateTime(doc.createdAt)}
                </div>
              </div>
              <div className="docup-item__actions">
                {(IMAGE_TYPES.includes(doc.mimeType) || doc.mimeType === 'application/pdf') && (
                  <button className="docup-btn preview bg-transparent border-0" style={{ color: 'var(--text)' }} onClick={() => handlePreview(doc)} title="Preview">
                    <Eye size={14} />
                  </button>
                )}
                <button className="docup-btn download bg-transparent border-0 text-primary" onClick={() => handleDownload(doc)} title="Download">
                  <Download size={14} />
                </button>
                {canDelete(doc) && (
                  <button className="docup-btn delete bg-transparent border-0 text-danger" onClick={() => handleDelete(docId, doc.name)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          )})}
        </div>
      )}

      {preview && (
        <div className="docup-preview-overlay" onClick={() => setPreview(null)} style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="docup-preview-modal bg-dark text-white" onClick={e => e.stopPropagation()}>
            <div className="docup-preview-head border-bottom border-secondary">
              <span>{preview.name}</span>
              <button className="docup-preview-close text-white bg-transparent border-0" onClick={() => setPreview(null)}>×</button>
            </div>
            <div className="docup-preview-body" style={{ background: '#000' }}>
              {IMAGE_TYPES.includes(preview.mimeType) ? (
                <img src={preview.url} alt={preview.name} className="docup-preview-img" style={{ maxHeight: '70vh', objectFit: 'contain' }} />
              ) : (
                <iframe src={preview.url} title={preview.name} className="docup-preview-iframe" style={{ height: '75vh', width: '100%', border: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
